import { Injectable } from '@angular/core';
import { WellnessOption } from '@constants/wellness';
import { action } from '@datorama/akita';
import { Address } from '@models/Address';
import { Contact } from '@models/Contact';
import { Quote } from '@models/Quote';
import { QuoteSession } from '@models/QuoteSession';
import { UtmParameters } from '@models/UtmParameters';

import { QuoteSessionStore } from './session.store';

@Injectable({ providedIn: 'root' })
export class QuoteSessionService {
	constructor(
		private readonly store: QuoteSessionStore,
	) { }

	@action('Reset Store')
	public reset(): void {
		this.store.reset();
	}

	@action('Update Full Session')
	public update(payload: Partial<QuoteSession>): void {
		this.store.update(state => ({
			...state,
			...payload,
			analyticsInfo: {
				...state.analyticsInfo,
				...payload.analyticsInfo,
			},
		}));
	}

	@action('Update UTM Parameters')
	public updateUTMParams(payload: Partial<UtmParameters>): void {
		this.store.update(state => ({
			...state,
			analyticsInfo: payload,
		}));
	}

	/**
	 * @deprecated
	 * Sets the brandId on the quoteDetails
	 * @param brandId the string id of the brand being set
	 */
	@action('Set Brand')
	public setBrand(brandId: string): void {
		this.store.update(state => ({
			...state,
			quoteDetails: {
				...state.quoteDetails,
				brandId,
			},
		}));
	}

	/** Sets the brand on the contact record */
	@action('Set Brand on Contact')
	public setBrandOnContact(brand: string): void {
		this.store.update(state => ({
			...state,
			contact: {
				...state.contact,
				brand,
			},
		}));
	}

	@action('Update Vet Search Zip')
	public updateVetSearchZip(zip: string): void {
		this.store.update(state => ({
			...state,
			contact: {
				...state.contact,
				vetSearchZip: zip,
			},
		}));
	}

	@action('Update Contact Info')
	public updateContactInfo(contactInfo: Pick<Contact, 'firstName' | 'lastName' >): void {
		this.store.update(state => ({
			...state,
			contact: {
				...state.contact,
				firstName: contactInfo.firstName,
				lastName: contactInfo.lastName,
			},
		}));
	}

	@action('Update Full Contact')
	public updateContact(payload: Partial<Contact>): void {
		this.store.update(state => ({
			...state,
			contact: {
				...state.contact,
				...payload,
			},
		}));
	}

	@action('Update Mailing Address')
	public updateMailingAddress(mailingAddress: Partial<Address>): void {
		this.store.update(state => ({
			...state,
			contact: {
				...state.contact,
				mailingAddress: {
					...state.contact.mailingAddress,
					...mailingAddress,
				},
			},
		}));
	}

	@action('Update Same Billing')
	public updateBillingSameAsMailing(payload: boolean): void {
		this.store.update(state => ({
			...state,
			contact: {
				...state.contact,
				billingSameAsMailing: payload,
			},
		}));
	}

	@action('Update Billing Address')
	public updateBillingAddress(billingAddress: Partial<Address>): void {
		this.store.update(state => ({
			...state,
			contact: {
				...state.contact,
				billingAddress: {
					...state.contact.billingAddress,
					...billingAddress,
				},
			},
		}));
	}

	@action('Update Physical Same As Mailing')
	public updatePhysicalSameAsMailing(payload: boolean): void {
		this.store.update(state => ({
			...state,
			contact: {
				...state.contact,
				physicalSameAsMailing: payload,
			},
		}));
	}

	@action('Update Physical Address')
	public updatePhysicalAddress(physicalAddress: Partial<Address>): void {
		this.store.update(state => ({
			...state,
			contact: {
				...state.contact,
				physicalAddress: {
					...state.contact.physicalAddress,
					...physicalAddress,
				},
			},
		}));
	}

	@action('Update Purchased Policy')
	public updatePurchasedPolicy(payload: Pick<Quote, 'purchaseSucceeded' | 'policyNumber'>): void {
		this.store.update(state => ({
			...state,
			quoteDetails: {
				...state.quoteDetails,
				...payload,
			},
		}));
	}

	@action('Update Email Address')
	public updateEmailAddress(email: string): void {
		this.store.update(state => ({
			...state,
			contact: {
				...state.contact,
				emailAddress: email,
			},
		}));
	}

	@action('Update Wellness Options')
	public updateWellnessOptions(options: WellnessOption[]): void {
		this.store.update(state => ({
			...state,
			wellnessOptions: [...options]
		}));
	}
}
